import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, analytics } from '../utils/analytics';

// Hook for automatic page view tracking
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView({
      page_path: location.pathname,
      page_title: document.title,
    });
  }, [location]);
};

// Hook for scroll depth tracking
export const useScrollTracking = (thresholds: number[] = [25, 50, 75, 90]) => {
  useEffect(() => {
    const trackedThresholds = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !trackedThresholds.has(threshold)) {
          trackedThresholds.add(threshold);
          analytics.trackScrollDepth(threshold);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [thresholds]);
};

// Hook for time on page tracking
export const useTimeTracking = (intervalSeconds: number = 30) => {
  const location = useLocation();

  useEffect(() => {
    const startTime = Date.now();
    let lastTrackedInterval = 0;

    const trackTimeInterval = () => {
      const currentTime = Date.now();
      const timeOnPage = Math.round((currentTime - startTime) / 1000);
      const currentInterval = Math.floor(timeOnPage / intervalSeconds);

      if (currentInterval > lastTrackedInterval) {
        lastTrackedInterval = currentInterval;
        analytics.trackTimeOnPage(timeOnPage, location.pathname);
      }
    };

    const intervalId = setInterval(trackTimeInterval, intervalSeconds * 1000);

    // Track final time on page when component unmounts
    return () => {
      clearInterval(intervalId);
      const finalTime = Math.round((Date.now() - startTime) / 1000);
      if (finalTime > intervalSeconds) {
        analytics.trackTimeOnPage(finalTime, location.pathname);
      }
    };
  }, [location.pathname, intervalSeconds]);
};

// Hook for enhanced analytics with multiple tracking features
export const useAnalytics = (options: {
  trackPageViews?: boolean;
  trackScrollDepth?: boolean;
  trackTimeOnPage?: boolean;
  scrollThresholds?: number[];
  timeInterval?: number;
} = {}) => {
  const {
    trackPageViews = true,
    trackScrollDepth = true,
    trackTimeOnPage = true,
    scrollThresholds = [25, 50, 75, 90],
    timeInterval = 30,
  } = options;

  // Enable page tracking
  if (trackPageViews) {
    usePageTracking();
  }

  // Enable scroll tracking
  if (trackScrollDepth) {
    useScrollTracking(scrollThresholds);
  }

  // Enable time tracking
  if (trackTimeOnPage) {
    useTimeTracking(timeInterval);
  }

  return analytics;
};